import React, { Fragment, useContext, useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import compose from '@shopify/react-compose'
import { withTranslation } from 'react-i18next'
import { withRouter } from 'react-router'

import { getInvoices } from '@portal/kit/pkg/services'

import { SubscriptionContext } from 'components/providers/subscription'

import { ContentBox, TableAttribute, Notification, withConfiguration } from '@portal/kit/pkg/components'
import { withEndUserData } from '@portal/kit/pkg/components'

import { formatPhoneNumber } from '@portal/kit/pkg/helpers'

import Invoices from 'components/invoices'
import TextLinks from 'components/text-links'

const phoneNumberValue = user => {
  if (!user.phone_number || user.phone_number.toString().length === 0) return ''
  return formatPhoneNumber(user.phone_number)
}

const BillingBox = ({ t, cardInfos :{card_type, suffix, expires_on} }) => {
  if(card_type && card_type.toLowerCase() === 'paypal')
  return <TableAttribute label={t('account.billing.is_not_card')} value={card_type || '-'} />

  return (<>
    <TableAttribute label={t('account.billing.is_card')} value={card_type || '-'} />
    <TableAttribute label={t('account.billing.card_number')} value={suffix || '-'}/>
    <TableAttribute label={t('account.billing.card_exp')} value={expires_on || '-'} />
  </>)
}

const Account = ({ user, t, location, portalConfiguration }) => {
  const [invoices, setInvoices] = useState([])
  const shouldHideAccount = portalConfiguration.should_hide_account
  const { subscriptions } = useContext(SubscriptionContext)
  const { billing_infos } = user

  useEffect(() => {
    if (!user) return

    fetchInvoices()
  }, [user])

  const fetchInvoices = async () => {
    try {
      const response = await getInvoices()
      setInvoices(response.data.invoices)
    } catch (error) {
      console.log(error)
    }
  }

  const cardInfos = useMemo(() => {
    if (!billing_infos || !billing_infos.credit_card) return

    const { credit_card: { card_type, suffix, expires_on } } = billing_infos
    return { card_type, suffix, expires_on }
  }, [billing_infos])

  return (
    <Fragment>
      {location.state && location.state.notification && (
        <Notification {...location.state.notification} />
      )}
      <ContentBox title={t('account.my_credentials')} titleIcon="key">
        <TableAttribute label={t('account.email')} value={user.email} editPath={shouldHideAccount ? null : "/my-account/email"} />
        {!shouldHideAccount && <TableAttribute label={t('account.password')} value="•••••••••" editPath="/my-account/password" /> }
        {user.properties && user.properties.id_pve &&
          <TableAttribute label={t('account.other')} value={t('account.mse_account')} />
        }
      </ContentBox>

      {!shouldHideAccount &&
        <ContentBox title={t('account.my_personal_info')} titleIcon="address-card">
          <TableAttribute label={t('account.firstname')} value={user.properties.first_name || user.properties.firstname} editPath="/my-account/name" />
          <TableAttribute label={t('account.lastname')} value={user.properties.last_name || user.properties.lastname} editPath="/my-account/name" />
          <TableAttribute label={t('account.phone_number.title')} value={phoneNumberValue(user)} editPath="/my-account/phone-number" />
        </ContentBox>
      }

      {invoices && invoices.length > 0 &&
        <ContentBox title={t('account.invoices.title')} titleIcon="file-invoice">
          <Invoices invoices={invoices} />
        </ContentBox>}

      {cardInfos &&
        <ContentBox title={t('account.billing.title')} titleIcon="credit-card">
          <BillingBox t={t} cardInfos={cardInfos} />
        </ContentBox>}

      {subscriptions &&
        <ContentBox title={t('account.my_docs')} titleIcon="file">
          <TextLinks />
        </ContentBox>}
    </Fragment>
  )
}

Account.propTypes = {
  user: PropTypes.object.isRequired,
}

export default compose(
  withTranslation(),
  withRouter,
  withConfiguration,
  withEndUserData,
)(Account)
